import { useUserContext } from '@/hooks/useUserContext';

/**
 * Returns whether the current user has administrator privileges.
 *
 * Reads role from `useUserContext()` (TanStack Query against `/api/v1/usercontext`).
 * Fails closed: if the role is unknown, missing, or still loading, `isAdmin` is `false`.
 *
 * ## Dev-only demo-role override
 *
 * In development (`import.meta.env.DEV`), QA can simulate a non-admin by running in the
 * browser console:
 *
 * ```
 * localStorage.setItem('demo.role', 'user');       // or 'view_only'
 * // then reload — useIsAdmin() will return false even if the real/mock user is administrator
 * localStorage.removeItem('demo.role');            // restore real role
 * ```
 *
 * Setting `demo.role` to `administrator` (or leaving it unset) keeps the real role.
 *
 * This override is intentionally localStorage-only and gated by `import.meta.env.DEV` —
 * it is compiled out of production builds and MUST NOT be exposed as a UI control.
 */
export function useIsAdmin(): { isAdmin: boolean; isLoading: boolean } {
  const { data, isLoading } = useUserContext();

  const realIsAdmin = data?.role?.toLowerCase() === 'administrator';

  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const override = window.localStorage.getItem('demo.role');
    if (override === 'user' || override === 'view_only') {
      return { isAdmin: false, isLoading };
    }
    // 'administrator' or unset → fall through to real value
  }

  return { isAdmin: realIsAdmin, isLoading };
}
